import React, { type FC } from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableCaption,
  Pagination,
  SkeletonLoader,
  Button,
  Icon
} from '@awell-health/design-system'
import { t } from 'i18next'
import { Link } from '@reach/router'
import { formatDate } from '../../../utils'
import { isNil } from 'lodash'
import type { PatientPathway } from '../types'

interface PatientPathwaysTableProps {
  pathways: PatientPathway[]
  loading?: boolean
  emptyMessage?: string
  totalCount?: number
  currentPage?: number
  perPage?: number
  onPageChange?: (page: number) => void
  onDeletePathway?: (pathway: PatientPathway) => void
  onStopPathway?: (pathway: PatientPathway) => void
  tableType: 'active' | 'completed' | 'stopped'
}

interface PathwayTableColumn {
  title: string
  field: string
  align?: 'left' | 'center' | 'right'
  width?: string
}

export const PatientPathwaysTable: FC<PatientPathwaysTableProps> = ({
  pathways,
  loading = false,
  emptyMessage = t('no_pathway_found'),
  totalCount = 0,
  currentPage = 1,
  perPage = 5,
  onPageChange,
  onDeletePathway,
  onStopPathway,
  tableType
}) => {
  const renderPathwayTitle = (pathway: PatientPathway) => (
    <Link to={`/pathway/${pathway.id}/activity-feed`} className="text-blue-600 hover:underline">
      {pathway.title}
    </Link>
  )

  const renderStartedBy = (pathway: PatientPathway) => {
    const createdBy = pathway.created?.user_name ?? pathway.created?.user_email
    return createdBy ?? 'Enrollment trigger'
  }

  const renderRelativeDate = (pathway: PatientPathway, field: keyof PatientPathway) => {
    const dateValue = pathway[field] as string
    return !isNil(dateValue) ? (formatDate(false)(dateValue) ?? '-') : '-'
  }

  const renderActions = (pathway: PatientPathway) => {
    if (tableType === 'active') {
      return (
        <div className="flex">
          {onStopPathway && (
            <Button variant="ghost" size="sm" shape="squareSm" onClick={() => onStopPathway(pathway)} className="p-1">
              <Icon icon="RiStopCircleFill" size={16} />
            </Button>
          )}
          {onDeletePathway && (
            <Button variant="ghost" size="sm" shape="squareSm" onClick={() => onDeletePathway(pathway)} className="p-1">
              <Icon icon="RiDeleteBinLine" size={16} />
            </Button>
          )}
        </div>
      )
    }

    return onDeletePathway ? (
      <Button
        variant="ghost"
        size="sm"
        onClick={() => onDeletePathway(pathway)}
        className="p-1 text-red-600 hover:text-red-800"
        title="Delete pathway"
      >
        <Icon icon="RiDeleteBinLine" size={16} />
      </Button>
    ) : null
  }

  const getColumns = (): PathwayTableColumn[] => {
    const baseColumns: PathwayTableColumn[] = [
      {
        title: t('pathway_table_column_pathway_name'),
        field: 'title'
      },
      {
        title: t('pathway_table_column_started_by'),
        field: 'startedBy'
      }
    ]

    if (tableType === 'active') {
      return [
        ...baseColumns,
        {
          title: t('active_activities'),
          field: 'activeActivities',
          align: 'center',
          width: 'w-30'
        },
        {
          title: t('failed_activities'),
          field: 'failedActivities',
          align: 'center',
          width: 'w-30'
        },
        {
          title: t('last_activity'),
          field: 'lastActivity',
          align: 'center',
          width: 'w-40'
        },
        {
          title: '',
          field: 'actions',
          width: 'w-25'
        }
      ]
    }

    if (tableType === 'completed') {
      return [
        ...baseColumns,
        {
          title: t('completed_date'),
          field: 'completedDate',
          align: 'center',
          width: 'w-40'
        },
        {
          title: t('last_activity'),
          field: 'lastActivity',
          align: 'center',
          width: 'w-40'
        },
        {
          title: '',
          field: 'actions',
          width: 'w-20'
        }
      ]
    }

    // stopped
    return [
      ...baseColumns,
      {
        title: t('stopped_date'),
        field: 'stoppedDate',
        align: 'center',
        width: 'w-40'
      },
      {
        title: t('reason'),
        field: 'reason'
      },
      {
        title: '',
        field: 'actions',
        width: 'w-20'
      }
    ]
  }

  const columns = getColumns()
  const totalPages = Math.ceil(totalCount / perPage)

  const renderCellContent = (pathway: PatientPathway, column: PathwayTableColumn) => {
    switch (column.field) {
      case 'title':
        return renderPathwayTitle(pathway)
      case 'startedBy':
        return renderStartedBy(pathway)
      case 'activeActivities':
        return pathway.active_activities ?? 0
      case 'failedActivities':
        return pathway.failed_activities ?? 0
      case 'lastActivity':
        return renderRelativeDate(pathway, 'latest_activity_date')
      case 'completedDate':
        return renderRelativeDate(pathway, 'complete_date')
      case 'stoppedDate':
        return renderRelativeDate(pathway, 'stop_date')
      case 'reason':
        return pathway.status_explanation ?? '-'
      case 'actions':
        return renderActions(pathway)
      default:
        return '-'
    }
  }

  if (loading) {
    return (
      <Table>
        <TableHeader>
          <TableRow>
            {columns.map(({ title, field }) => (
              <TableHead key={field}>{title}</TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {[...Array(5)].map((_, i) => (
            <TableRow key={i}>
              {columns.map((_, j) => (
                <TableCell key={j} className="h-14">
                  <SkeletonLoader className="h-4 w-full" />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
        <TableCaption className="py-0">
          <Pagination
            totalCount={totalCount}
            onPageChange={onPageChange || (() => {})}
            perPage={perPage}
            page={currentPage}
          />
        </TableCaption>
      </Table>
    )
  }

  if (pathways.length === 0) {
    return (
      <Table>
        <TableHeader>
          <TableRow>
            {columns.map(({ title, field }) => (
              <TableHead key={field}>{title}</TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          <TableRow>
            <TableCell colSpan={columns.length} className="text-center py-8 text-gray-500 font-normal">
              {emptyMessage}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    )
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          {columns.map(({ title, field, align = 'left', width }) => (
            <TableHead key={field} className={align === 'center' ? `text-center ${width}` : `${width}`}>
              {title}
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {pathways.map(pathway => (
          <TableRow key={pathway.id}>
            {columns.map(column => (
              <TableCell
                key={column.field}
                className={column.align === 'center' ? 'text-center font-normal' : 'font-normal'}
              >
                {renderCellContent(pathway, column)}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
      {totalPages > 1 && (
        <TableCaption className="py-0">
          <Pagination
            totalCount={totalCount}
            onPageChange={onPageChange || (() => {})}
            perPage={perPage}
            page={currentPage}
          />
        </TableCaption>
      )}
    </Table>
  )
}
